import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Show, SignIn } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, errMsg } from "@/lib/api";

function PasswordForm() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password: pw });
      window.location.assign(`/sign-in?__clerk_ticket=${encodeURIComponent(r.data.ticket)}`);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-sm bg-white border border-slate-200 p-8 space-y-4" data-testid="signin-form">
      <h1 className="font-serif text-3xl text-slate-900">Sign in</h1>
      <div className="space-y-1.5">
        <label className="small-label" htmlFor="si-email">Email</label>
        <Input id="si-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" data-testid="signin-email-input" />
      </div>
      <div className="space-y-1.5">
        <label className="small-label" htmlFor="si-pw">Password</label>
        <Input id="si-pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-sm" data-testid="signin-password-input" />
      </div>
      {error && <p className="text-sm text-red-700" data-testid="signin-error">{error}</p>}
      <Button type="submit" disabled={busy} className="w-full rounded-sm h-11" data-testid="signin-submit-button">{busy ? "Signing in…" : "Sign in"}</Button>
      <div className="flex justify-between text-sm text-slate-500">
        <Link to="/sign-up" className="underline" data-testid="signin-signup-link">Create a login</Link>
        <Link to="/sign-in/reset" className="underline" data-testid="signin-forgot-link">Forgot password?</Link>
      </div>
    </form>
  );
}

export default function SignInPage() {
  const [params] = useSearchParams();
  if (params.get("__clerk_ticket")) return <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" />;
  return (
    <>
      <Show when="signed-in"><Navigate to="/" replace /></Show>
      <Show when="signed-out"><PasswordForm /></Show>
    </>
  );
}

export function ResetPassword() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (e, fn) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(errMsg(err));
    }
    setBusy(false);
  };
  const send = (e) => run(e, async () => { await axios.post(`${API}/auth/forgot`, { email }); setStep("code"); });
  const reset = (e) => run(e, async () => {
    const r = await axios.post(`${API}/auth/reset`, { email, code, password: pw });
    window.location.assign(`/sign-in?__clerk_ticket=${encodeURIComponent(r.data.ticket)}`);
  });
  return (
    <form onSubmit={step === "email" ? send : reset} className="w-full max-w-sm bg-white border border-slate-200 p-8 space-y-4" data-testid="reset-form">
      <h1 className="font-serif text-3xl text-slate-900">Reset password</h1>
      {step === "email" ? (
        <>
          <p className="text-sm text-slate-600" data-testid="reset-hint">Enter your login email. If it has an account, we'll email you a 6-digit code.</p>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-sm" data-testid="reset-email-input" />
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600" data-testid="reset-sent">If <b>{email}</b> has an account, a code is on its way. It expires in 15 minutes.</p>
          <Input inputMode="numeric" required maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="rounded-sm font-mono tracking-[0.3em]" data-testid="reset-code-input" />
          <Input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="rounded-sm" data-testid="reset-password-input" />
        </>
      )}
      {error && <p className="text-sm text-red-700" data-testid="reset-error">{error}</p>}
      <Button type="submit" disabled={busy} className="w-full rounded-sm h-11" data-testid="reset-submit-button">
        {busy ? "Please wait…" : step === "email" ? "Email me a code" : "Set new password and sign in"}
      </Button>
      <div className="flex justify-between text-sm text-slate-500">
        <Link to="/sign-in" className="underline" data-testid="reset-back-link">Back to sign in</Link>
        {step === "code" && <button type="button" className="underline" onClick={() => setStep("email")} data-testid="reset-resend">Send a new code</button>}
      </div>
    </form>
  );
}
